// Region detection for affiliate links. Auto-detect (timezone/locale),
// manual override persisted in localStorage, default US. SSR-safe.
import { useEffect, useState } from 'react'

export type Region = 'ca' | 'us'
const KEY = 'rs_region'
const CA_TZ = /America\/(Toronto|Montreal|Vancouver|Edmonton|Winnipeg|Halifax|Regina|St_Johns|Moncton|Glace_Bay|Goose_Bay|Iqaluit|Whitehorse|Yellowknife|Dawson|Rankin_Inlet|Resolute|Atikokan|Blanc-Sablon|Creston|Fort_Nelson)/

export function detectRegion(): Region {
  if (typeof window === 'undefined') return 'us'
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (CA_TZ.test(tz)) return 'ca'
    const lang = navigator.language || ''
    if (/-CA$/i.test(lang)) return 'ca'
  } catch {
    /* noop */
  }
  return 'us'
}

export function getRegion(): Region {
  if (typeof window === 'undefined') return 'us'
  const saved = window.localStorage.getItem(KEY)
  if (saved === 'ca' || saved === 'us') return saved
  return detectRegion()
}

export function setRegion(r: Region) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(KEY, r)
    window.dispatchEvent(new CustomEvent('rs-region', { detail: r }))
  }
}

export function useRegion(): [Region, (r: Region) => void] {
  const [region, setRegionState] = useState<Region>('us')
  useEffect(() => {
    setRegionState(getRegion())
    const handler = (e: Event) => setRegionState((e as CustomEvent).detail as Region)
    window.addEventListener('rs-region', handler)
    return () => window.removeEventListener('rs-region', handler)
  }, [])
  const update = (r: Region) => {
    setRegion(r)
    setRegionState(r)
  }
  return [region, update]
}
